/**
 * File upload service for handling S3 uploads via presigned URLs
 */

import { api } from './api'

export interface FileUploadRequest {
  original_filename: string
  file_size: number
  content_type: string
  data_source_id: number
}

export interface PresignedUrlResponse {
  upload_id: number
  presigned_url: string
  expires_at: string
  s3_key: string
}

export interface FileUploadResponse {
  id: number
  data_source_id?: number
  original_filename?: string
  file_size?: number
  content_type?: string
  file_path_s3?: string
  s3_bucket?: string
  s3_key?: string
  upload_status?: 'PENDING' | 'UPLOADING' | 'COMPLETED' | 'FAILED'
  upload_initiated_at?: string
  upload_completed_at?: string
  upload_error_message?: string
  presigned_url_expires_at?: string
  file_contents_preview?: string
  created_at: string
  updated_at: string
  created_by?: string
  updated_by?: string
  deleted_at?: string
  deleted_by?: string
}

export interface FileUploadProgress {
  upload_id: number
  filename: string
  status: 'PENDING' | 'UPLOADING' | 'COMPLETED' | 'FAILED'
  progress: number
  error?: string
  s3_uri?: string
}

export type FileUploadProgressCallback = (progress: FileUploadProgress) => void

/**
 * Upload a file to S3 using presigned URLs
 */
export async function uploadFileToS3(
  file: File,
  dataSourceId: number,
  onProgress?: FileUploadProgressCallback
): Promise<FileUploadResponse> {
  if (!dataSourceId) {
    throw new Error('Data source ID is required')
  }

  // Step 1: Get presigned URL from backend
  onProgress?.({
    upload_id: 0,
    filename: file.name,
    status: 'PENDING',
    progress: 5
  })

  const initiateResponse = await api.post<{
    success: boolean
    message: string
    data: PresignedUrlResponse
  }>(`/data-sources/${dataSourceId}/uploads/initiate`, {
    original_filename: file.name,
    file_size: file.size,
    content_type: file.type,
    data_source_id: dataSourceId
  })

  const { upload_id, presigned_url } = initiateResponse.data.data

  onProgress?.({
    upload_id,
    filename: file.name,
    status: 'UPLOADING',
    progress: 10
  })

  try {
    // Step 2: Mark upload as uploading
    await api.post(`/data-sources/uploads/${upload_id}/mark-uploading`)

    // Step 3: Upload file directly to S3 using presigned URL
    const contentType = file.type || 'application/octet-stream'
    console.log('Uploading to S3 with:', {
      presigned_url: presigned_url.substring(0, 50) + '...',
      file_name: file.name,
      file_size: file.size,
      content_type: contentType
    })

    // Try upload with minimal headers first
    // S3 presigned URLs may already include content-type in signature
    const uploadResponse = await fetch(presigned_url, {
      method: 'PUT',
      body: file
    })

    console.log('S3 Upload Response Status:', uploadResponse.status)
    console.log('S3 Upload Response Headers:', {
      'content-type': uploadResponse.headers.get('content-type'),
      'etag': uploadResponse.headers.get('etag'),
    })

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text()
      console.error('S3 Upload Error Response:', errorText)
      throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`)
    }

    onProgress?.({
      upload_id,
      filename: file.name,
      status: 'UPLOADING',
      progress: 80
    })

    // Step 4: Mark upload as completed
    await api.post(`/data-sources/uploads/${upload_id}/mark-completed`)

    onProgress?.({
      upload_id,
      filename: file.name,
      status: 'COMPLETED',
      progress: 100
    })

    // Step 5: Get final upload details
    const finalResponse = await api.get<{
      success: boolean
      message: string
      data: FileUploadResponse
    }>(`/data-sources/uploads/${upload_id}`)

    return finalResponse.data.data

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Upload failed'

    // Try to update backend status to failed
    try {
      await api.patch(`/data-sources/uploads/${upload_id}`, {
        upload_status: 'FAILED',
        upload_error_message: errorMessage
      })
    } catch (updateError) {
      console.error('Failed to update upload status:', updateError)
    }

    onProgress?.({
      upload_id,
      filename: file.name,
      status: 'FAILED',
      progress: 0,
      error: errorMessage
    })

    throw error
  }
}

/**
 * Get file upload details by ID
 */
export async function getFileUpload(uploadId: number): Promise<FileUploadResponse> {
  const response = await api.get<{
    success: boolean
    message: string
    data: FileUploadResponse
  }>(`/data-sources/uploads/${uploadId}`)
  return response.data.data
}

/**
 * Update file upload status
 */
export async function updateFileUploadStatus(
  uploadId: number,
  status: 'PENDING' | 'UPLOADING' | 'COMPLETED' | 'FAILED',
  errorMessage?: string
): Promise<FileUploadResponse> {
  const response = await api.patch<{
    success: boolean
    message: string
    data: FileUploadResponse
  }>(`/data-sources/uploads/${uploadId}`, {
    upload_status: status,
    upload_error_message: errorMessage
  })
  return response.data.data
}

/**
 * Get supported file extensions
 */
export function getSupportedExtensions(): string[] {
  return ['csv', 'xlsx', 'xls', 'json', 'xml']
}

/**
 * Check if file type is supported
 */
export function isSupportedFileType(file: File): boolean {
  const extension = file.name.split('.').pop()?.toLowerCase()
  const supportedExtensions = getSupportedExtensions()

  const allowedTypes = [
    'text/csv',
    'application/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/json',
    'application/xml',
    'text/xml'
  ]

  return allowedTypes.includes(file.type) || supportedExtensions.includes(extension || '')
}

/**
 * Format file size in human readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * Validate file for upload
 */
export function validateFile(file: File): string | null {
  // Check file type
  if (!isSupportedFileType(file)) {
    return 'Please upload a valid file (CSV, Excel, JSON, or XML)'
  }

  // Check file size (max 10MB)
  if (file.size > 10 * 1024 * 1024) {
    return 'File size must be less than 10MB'
  }

  // Check if file is empty
  if (file.size === 0) {
    return 'File cannot be empty'
  }

  return null
}